import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { inject, observer } from 'mobx-react';
import { Button, CalloutOutline, Flex, Heading, Icon, Link, Text } from 'core/components';
import { Checkbox, Field, FormComponent, Select, MultiSelect, TextArea } from 'core/form';
import $forms from 'core/form/util/$forms';
import { persistHash } from 'app/stores/query/urlHash';

const REPORT_TYPES = {
  REPORT_ISSUE: 'report issue',
  CREDITS: 'credits',
  KUBE_REQUEST: 'kube request',
  JOURNEYS_REQUEST: 'journeys request',
  AI_REQUEST: 'ai request',
  ADD_POPULATORS_REQUEST: 'add populators request',
  TRAINING_REQUEST: 'training request',
  ACCOUNT_QUESTION: 'account question',
  PRODUCT_QUESTION: 'product question',
  BUG: 'bug',
  DEVICE_SUPPORT: 'device support',
  UI_UX_ISSUE: 'ui/ux issue',
  FEATURE_REQUEST: 'feature request'
};

const reportTypeToLabelMap = {
  [REPORT_TYPES.REPORT_ISSUE]: 'Help and Support',
  [REPORT_TYPES.CREDITS]: 'Add Synthetics Test Credits',
  [REPORT_TYPES.KUBE_REQUEST]: 'Request Kentik Kube access',
  [REPORT_TYPES.JOURNEYS_REQUEST]: 'Request Journeys access',
  [REPORT_TYPES.AI_REQUEST]: 'Request Kentik AI access',
  [REPORT_TYPES.ADD_POPULATORS_REQUEST]: 'Add Populators for Custom Dimensions',
  [REPORT_TYPES.TRAINING_REQUEST]: 'Training',
  [REPORT_TYPES.ACCOUNT_QUESTION]: 'Account Question',
  [REPORT_TYPES.PRODUCT_QUESTION]: 'Product Question',
  [REPORT_TYPES.BUG]: 'Bug',
  [REPORT_TYPES.DEVICE_SUPPORT]: 'NMS New Metric Request',
  [REPORT_TYPES.UI_UX_ISSUE]: 'UI/UX Issue',
  [REPORT_TYPES.FEATURE_REQUEST]: 'Feature Request'
};

const deviceTypeToLabelMap = {
  router: 'Router',
  switch: 'Switch',
  firewall: 'Firewall',
  'load balancer': 'Load Balancer',
  wap: 'Wireless Access Point (AP)',
  'vpn concentrator': 'VPN Concentrator',
  server: 'Server (Bare Metal & Virtual)',
  'cloud gateway & virtual': 'Cloud Gateway & Virtual Appliance',
  'sd-wan': 'SD-WAN Appliance',
  'edge device': 'Edge Computing Device',
  other: 'Other'
};

@inject('$auth', '$app')
@observer
class ReportIssuePopoverContent extends Component {
  constructor(props) {
    super();

    const { form, $app } = props;
    const { reportIssuePopoverProps } = $app;
    const formValues = {};

    [
      'type',
      'summary',
      'description',
      'device_type',
      'bug_bounty',
      'for_customer',
      'customer_email',
      'observ_needs',
      'other_details',
      'other_device'
    ].forEach((key) => {
      if (reportIssuePopoverProps?.[key]) {
        formValues[key] = reportIssuePopoverProps[key];
      }
    });

    form.setValues(formValues);
  }

  componentWillUnmount() {
    this.saveFormStateToAppStore();
  }

  handleSubmit = () => {
    const { form, $app, location, match } = this.props;
    const { platform, userAgent } = window.navigator;
    const { pathname } = location;
    const { clientWidth, clientHeight } = document.querySelector('body');

    const payload = {
      clientHeight,
      clientWidth,
      info: {
        history: $app.routeHistory,
        ...form.getValues(),
        allForms: $forms.getAllUnsuppressedFormValues(),
        typeTitle: reportTypeToLabelMap[form.getValue('type')]
      },
      pathname,
      platform,
      userAgent,
      isNext: $app.isNext
    };

    if ($app.productArea) {
      payload.productArea = $app.productArea;
    } else {
      payload.productArea = 'unknown';
    }

    $app.submitReportIssue(payload).then(() => form.reset());

    const { urlHash, hash, queryHash } = match?.params || {};
    if (urlHash || hash || queryHash) {
      persistHash(urlHash || hash || queryHash);
    }
  };

  saveFormStateToAppStore = () => {
    const { form, $app } = this.props;

    const formValues = {};

    form.fieldStates.forEach((field) => {
      formValues[field.name] = field.value;
    });

    $app.setReportIssuePopoverProps(formValues);
  };

  handleClose = () => {
    const { $app } = this.props;
    $app.toggleReportIssuePopover(false);
  };

  trackFeatureRequest = (e) => {
    e.preventDefault();

    window.pendo.feedback.loginAndRedirect({ anchor: { nodeName: 'a' } });

    return false;
  };

  isTypeUserSelectable(type) {
    const { userSelectableTypeOptions } = this.props;
    return !type || userSelectableTypeOptions.some((option) => option.value === type);
  }

  isDeviceTypeUserSelectable(device_type) {
    const { userSelectableDeviceTypeOptions } = this.props;
    return !device_type || Array.from(userSelectableDeviceTypeOptions).map((option) => option.value === device_type);
  }

  /**
   * Makes conditional updates to dependent field "device_type" when "type" field changes
   * Avoids submitting stale data in "device_type" field when user changes "type" AFTER setting "device_type"
   * @param {FieldState} fieldState
   * @param {String} newValue
   */
  handleTypeChange = (fieldState, newValue) => {
    const { form } = this.props;

    const isNotDeviceSupport = newValue !== REPORT_TYPES.DEVICE_SUPPORT;

    const deviceTypeValue = form.getValue('device_type');
    const shouldUnsetDeviceType = isNotDeviceSupport && deviceTypeValue?.length > 0;

    if (shouldUnsetDeviceType) {
      form.setValue('device_type', []);
    }

    const observeNeedsValue = form.getValue('observ_needs');
    const shouldUnsetObserveNeeds = isNotDeviceSupport && observeNeedsValue?.length > 0;

    if (shouldUnsetObserveNeeds) {
      form.setValue('observ_needs', '');
    }

    const otherDetailsValue = form.getValue('other_details');
    const shouldUnsetOtherDetails = isNotDeviceSupport && otherDetailsValue?.length > 0;

    if (shouldUnsetOtherDetails) {
      form.setValue('other_details', '');
    }

    fieldState.setValue(newValue);
  };

  /**
   * Makes conditional updates to dependent field "customer_email" when "for_customer" field changes
   * Avoids submitting stale data in "customer_email" field when user sets "{for_customer: false}" AFTER setting "customer_email"
   * @param {FieldState} fieldState
   * @param {String} newValue
   */
  handleForCustomerChange = (fieldState, newValue) => {
    const { form } = this.props;

    const shouldUnsetCustomerEmail = newValue === false && !!form.getValue('customer_email');

    if (shouldUnsetCustomerEmail) {
      form.setValue('customer_email', '');
    }

    fieldState.setValue(newValue);
  };

  render() {
    const { form, $app, $auth } = this.props;
    const { sendingIssueRequest } = $app;

    const type = form.getValue('type');
    const device_type = form.getValue('device_type');
    const title = reportTypeToLabelMap[type];
    const customer = form.getValue('for_customer');

    return (
      <Flex flexDirection="column" gap={2} mt="12px" maxWidth={500}>
        {type !== 'feature request' && (
          <Flex
            flexDirection="column"
            height="calc(-100px + 100vh)"
            max-height="calc(-100px + 100vh)"
            overflow="auto"
            display="flex"
            gap={1}
            mt="12px"
            maxWidth={500}
          >
            <Heading
              display="flex"
              alignItems="center"
              justifyContent={title ? 'space-between' : 'end'}
              px={3}
              py={1}
              level={3}
              m={0}
              borderBottom="thin"
            >
              {title}
              <Button icon="cross" onClick={this.handleClose} title="Close" minimal />
            </Heading>

            <Flex flexDirection="column" px={3} gap={2}>
              <Heading
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                level={5}
                borderBottom="thin"
                pb="4px"
                m={0}
              >
                Check our Knowledge Base
              </Heading>
              <CalloutOutline intent="primary" flex={1}>
                <Flex alignItems="center" gap={2}>
                  <Icon icon="help" iconSize={20} color="primary" />
                  <Flex flexDirection="column">
                    <Text as="div" small>
                      Before submitting a support request...
                    </Text>
                    <Link
                      blank
                      href={`https://kb.kentik.com/zDir/HelpHandler.htm?portalPath=${encodeURIComponent(
                        window.location.pathname
                      )}`}
                    >
                      See the Knowledge Base entry for this page. <Icon icon="new-tab" iconSize={12} minimal small />
                    </Link>
                  </Flex>
                </Flex>
              </CalloutOutline>
            </Flex>

            <Flex flexDirection="column" px={3} gap={2} pb={$app.isSubtenant ? 3 : 0}>
              <Heading
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                level={5}
                borderBottom="thin"
                pb="4px"
                m={0}
              >
                Submit a Support Request
              </Heading>

              {(type !== 'feature request' || $app.isSubtenant) && (
                <Field name="summary" disabled={sendingIssueRequest} large m={0}>
                  <TextArea rows={2} fill />
                </Field>
              )}

              {this.isTypeUserSelectable(type) && (
                <Field name="type" disabled={sendingIssueRequest} large m={0} onChange={this.handleTypeChange}>
                  <Select menuWidth={200} />
                </Field>
              )}

              {type === REPORT_TYPES.DEVICE_SUPPORT && (
                <Text muted style={{ fontStyle: 'italic' }}>
                  Request new NMS device or additional NMS metrics only
                </Text>
              )}

              {type === REPORT_TYPES.DEVICE_SUPPORT && this.isDeviceTypeUserSelectable(device_type) && (
                <Field name="device_type" disabled={sendingIssueRequest} large m={0}>
                  <MultiSelect menuWidth={200} />
                </Field>
              )}

              {device_type.includes(' Other') && type === REPORT_TYPES.DEVICE_SUPPORT && (
                <Field name="other_device" disabled={sendingIssueRequest} showLabel={false} large m={0}>
                  <TextArea rows={1} />
                </Field>
              )}

              {type === REPORT_TYPES.DEVICE_SUPPORT && (
                <Field name="observ_needs" disabled={sendingIssueRequest} large m={0}>
                  <TextArea rows={4} fill />
                </Field>
              )}

              {type === REPORT_TYPES.DEVICE_SUPPORT && (
                <Field name="other_details" disabled={sendingIssueRequest} large m={0}>
                  <TextArea rows={2} fill />
                </Field>
              )}

              {(type !== REPORT_TYPES.FEATURE_REQUEST || $app.isSubtenant) && (
                <Field name="description" disabled={sendingIssueRequest} large m={0}>
                  <TextArea rows={6} fill />
                </Field>
              )}

              {$app.isNext && ($auth.hasSudo || $auth.isSpoofed) && (
                <Field name="bug_bounty" showLabel={false} large m={0}>
                  <Checkbox showBoxLabel />
                </Field>
              )}

              {($auth.hasSudo || $auth.isSpoofed) && (
                <Field name="for_customer" showLabel={false} large m={0} onChange={this.handleForCustomerChange}>
                  <Checkbox showBoxLabel />
                </Field>
              )}

              {customer && (
                <Field name="customer_email" disabled={sendingIssueRequest} large m={0}>
                  <TextArea rows={1} fill />
                </Field>
              )}

              {(type !== REPORT_TYPES.FEATURE_REQUEST || $app.isSubtenant) && (
                <Flex flexDirection="column" gap={2} px={0} pb={0}>
                  <Flex flexDirection="column">
                    <Button
                      fill
                      disabled={!form.valid || !form.dirty}
                      intent={!form.valid ? 'none' : 'primary'}
                      loading={sendingIssueRequest}
                      text="Submit"
                      onClick={this.handleSubmit}
                    />

                    {!$app.isSubtenant && (
                      <Link
                        class=".bp5-running-text"
                        small
                        px={0}
                        blank
                        href="/api/ui/zendesk/jwt_token?return_to=https://kentik.zendesk.com/hc/requests"
                      >
                        <h4>Track your support requests</h4>
                      </Link>
                    )}
                  </Flex>
                </Flex>
              )}

              {!$app.isSubtenant && (
                <Flex flexDirection="column" gap={2} px={0} pb={3}>
                  <Flex flexDirection="column">
                    <Text muted>What do you wish you could do with Kentik?</Text>
                    <Text muted>Share your dream features!</Text>
                  </Flex>
                  <Button
                    fill
                    mt={1}
                    loading={sendingIssueRequest}
                    text="Manage or Submit Feature Requests"
                    onClick={this.trackFeatureRequest}
                  />
                </Flex>
              )}
            </Flex>
          </Flex>
        )}

        {type === REPORT_TYPES.FEATURE_REQUEST && !$app.isSubtenant && (
          <Flex flexDirection="column" height="390px" overflow="auto" display="flex" gap={2} mt="12px" maxWidth={500}>
            <Heading
              display="flex"
              alignItems="center"
              justifyContent={title ? 'space-between' : 'end'}
              px={3}
              py={1}
              level={3}
              m={0}
              borderBottom="thin"
            >
              {title}
              <Button icon="cross" onClick={this.handleClose} title="Close" minimal />
            </Heading>

            <Flex flexDirection="column" px={3} gap={2}>
              <Heading
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                level={5}
                borderBottom="thin"
                pb="4px"
                m={0}
              >
                Check our Knowledge Base
              </Heading>
              <CalloutOutline intent="primary" flex={1}>
                <Flex alignItems="center" gap={2}>
                  <Icon icon="help" iconSize={20} color="primary" />
                  <Flex flexDirection="column">
                    <Text as="div" small>
                      Before submitting a support request...
                    </Text>
                    <Link
                      blank
                      href={`https://kb.kentik.com/zDir/HelpHandler.htm?portalPath=${encodeURIComponent(
                        window.location.pathname
                      )}`}
                    >
                      See the Knowledge Base entry for this page. <Icon icon="new-tab" iconSize={12} minimal small />
                    </Link>
                  </Flex>
                </Flex>
              </CalloutOutline>
            </Flex>

            <Flex flexDirection="column" px={3} gap={2} pb={$app.isSubtenant ? 3 : 0}>
              <Heading
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                level={5}
                borderBottom="thin"
                pb="4px"
                m={0}
              >
                Submit a Support Request
              </Heading>

              {this.isTypeUserSelectable(type) && (
                <Field name="type" disabled={sendingIssueRequest} large m={0}>
                  <Select menuWidth={200} />
                </Field>
              )}
            </Flex>
            <Flex flexDirection="column" gap={2} px={3} pb={3}>
              <Flex flexDirection="column">
                <Text muted>What do you wish you could do with Kentik?</Text>
                <Text muted>Share your dream features!</Text>
              </Flex>
              <Button
                fill
                mt={1}
                loading={sendingIssueRequest}
                text="Manage or Submit Feature Requests"
                onClick={this.trackFeatureRequest}
              />
            </Flex>
          </Flex>
        )}
      </Flex>
    );
  }
}

// outer component to observe isSubtenant & then pass in updated fields
const ReportIssuePopover = inject('$app')(
  observer((props) => {
    const userSelectableTypeOptions = [
      { label: reportTypeToLabelMap[REPORT_TYPES.ACCOUNT_QUESTION], value: REPORT_TYPES.ACCOUNT_QUESTION },
      { label: reportTypeToLabelMap[REPORT_TYPES.PRODUCT_QUESTION], value: REPORT_TYPES.PRODUCT_QUESTION },
      { label: reportTypeToLabelMap[REPORT_TYPES.BUG], value: REPORT_TYPES.BUG },
      { label: reportTypeToLabelMap[REPORT_TYPES.DEVICE_SUPPORT], value: REPORT_TYPES.DEVICE_SUPPORT },
      { label: reportTypeToLabelMap[REPORT_TYPES.TRAINING_REQUEST], value: REPORT_TYPES.TRAINING_REQUEST },
      { label: reportTypeToLabelMap[REPORT_TYPES.UI_UX_ISSUE], value: REPORT_TYPES.UI_UX_ISSUE },
      { label: reportTypeToLabelMap[REPORT_TYPES.FEATURE_REQUEST], value: REPORT_TYPES.FEATURE_REQUEST }
    ];

    const userSelectableDeviceTypeOptions = [
      { label: deviceTypeToLabelMap.router, value: ' Router' },
      { label: deviceTypeToLabelMap.switch, value: ' Switch' },
      { label: deviceTypeToLabelMap.firewall, value: ' Firewall' },
      { label: deviceTypeToLabelMap['load balancer'], value: ' Load Balancer' },
      { label: deviceTypeToLabelMap.wap, value: ' Wireless Access Point (AP)' },
      { label: deviceTypeToLabelMap['vpn concentrator'], value: ' VPN Concentrator' },
      { label: deviceTypeToLabelMap.server, value: ' Server (Bare Metal & Virtual)' },
      { label: deviceTypeToLabelMap['cloud gateway & virtual'], value: ' Cloud Gateway & Virtual Appliance' },
      { label: deviceTypeToLabelMap['sd-wan'], value: ' SD-WAN Appliance' },
      { label: deviceTypeToLabelMap['edge device'], value: ' Edge Computing Device' },
      { label: deviceTypeToLabelMap.other, value: ' Other' }
    ];

    const fields = {
      type: {
        label: 'Type',
        rules: 'required',
        defaultValue: '',
        validateOptions: false, // Since we allow anything that's in reportTypeToLabelMap
        // Request Type: Question, Bug, Training, (MKP) Feature Request
        options: userSelectableTypeOptions
      },
      summary: {
        label: 'Summary',
        rules: 'required',
        placeholder: 'What can we help you with?'
      },
      description: {
        label: 'Additional Details',
        rules: 'required',
        placeholder: 'Please enter additional relevant details.'
      },
      bug_bounty: {
        label: 'Bug Bounty'
      },
      for_customer: {
        label: 'Report on Behalf of Customer'
      },
      customer_email: {
        label: 'Customer Email',
        rules: [{ required_if: ['for_customer', true] }, 'email'],
        placeholder: 'enter customer email OR your email'
      },
      device_type: {
        label: 'Device Type',
        defaultValue: '',
        validateOptions: false, // Similar to Type
        options: userSelectableDeviceTypeOptions
      },
      other_device: {
        label: 'Other Device',
        placeholder: 'enter other device type'
      },
      observ_needs: {
        label: 'Observability Needs',
        placeholder: 'vitality (state, cpu, memory, storage), interface stats, traps/syslog, routing info, etc'
      },
      other_details: {
        label: 'Other Technical Details',
        placeholder: 'Example: OIDs/YANG paths'
      }
    };

    const options = {
      name: 'Report Issue',
      suppressFormValues: true
    };

    return (
      <FormComponent fields={fields} options={options}>
        {(formProps) => (
          <ReportIssuePopoverContent
            userSelectableTypeOptions={userSelectableTypeOptions}
            userSelectableDeviceTypeOptions={userSelectableDeviceTypeOptions}
            {...props}
            {...formProps}
          />
        )}
      </FormComponent>
    );
  })
);

export default withRouter(ReportIssuePopover);
